import { useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { isOidcEnabled, startMicrosoftSignIn } from '@/auth/oidc'
import { AppVersion } from '@/components/AppVersion'

const DEV_SEED_PASSWORD = 'AriesDevPassword123!'

const DEV_SEED_USERS = [
  { email: 'admin@veersatech.com', role: 'admin' },
  { email: 'finance@veersatech.com', role: 'finance' },
  { email: 'owner1@veersatech.com', role: 'owner' },
  { email: 'viewer@veersatech.com', role: 'viewer' },
] as const

const ABOUT_BLURB =
  'ARIES is Veersa’s Agentic Revenue Intelligence & Estimation System — a ' +
  'secure workspace where Finance and opportunity owners plan, weight, and ' +
  'publish the revenue forecast with clear auditability.'

function SignInVisual() {
  return (
    <svg
      className="sign-in__visual"
      viewBox="0 0 480 360"
      role="img"
      aria-label="Abstract illustration of forecast charts and connected teams"
    >
      <defs>
        <linearGradient id="signInGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#e5002b" stopOpacity="0.35" />
        </linearGradient>
      </defs>
      <rect width="480" height="360" fill="url(#signInGlow)" opacity="0.25" />
      <g fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5">
        <polyline points="48,280 120,210 180,230 250,140 320,170 400,90" />
        <polyline points="48,300 110,270 190,290 260,220 340,240 400,180" opacity="0.55" />
      </g>
      <g fill="rgba(255,255,255,0.92)">
        <circle cx="120" cy="210" r="5" />
        <circle cx="250" cy="140" r="5" />
        <circle cx="400" cy="90" r="5" />
      </g>
      <g fill="rgba(255,255,255,0.12)">
        <rect x="56" y="48" width="140" height="72" rx="12" />
        <rect x="220" y="48" width="100" height="72" rx="12" />
        <rect x="340" y="48" width="84" height="72" rx="12" />
      </g>
      <g fill="#fff" fontFamily="IBM Plex Sans, sans-serif" fontSize="11" opacity="0.85">
        <text x="72" y="78">
          Pipeline
        </text>
        <text x="72" y="96" fontSize="18" fontWeight="600">
          Weighted
        </text>
        <text x="236" y="78">
          Committed
        </text>
        <text x="236" y="96" fontSize="18" fontWeight="600">
          Clear
        </text>
        <text x="356" y="78">
          Published
        </text>
        <text x="356" y="96" fontSize="18" fontWeight="600">
          Audit
        </text>
      </g>
    </svg>
  )
}

export function SignInPage() {
  const { isAuthenticated, signIn } = useAuth()
  const location = useLocation()
  const oidc = isOidcEnabled()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [msPending, setMsPending] = useState(false)
  const [breakGlassOpen, setBreakGlassOpen] = useState(false)

  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? '/'
    return <Navigate to={from} replace />
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await signIn(email, password)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleMicrosoft() {
    setError(null)
    setMsPending(true)
    try {
      await startMicrosoftSignIn()
    } catch (caught) {
      setMsPending(false)
      setError(caught instanceof Error ? caught.message : 'Could not start Microsoft sign-in.')
    }
  }

  function fillDevUser(nextEmail: string) {
    setEmail(nextEmail)
    setPassword(DEV_SEED_PASSWORD)
    setError(null)
  }

  const passwordForm = (
    <form className="sign-in__password-form" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Email</span>
        <input
          className="field__input"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Password</span>
        <input
          className="field__input"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>

      <button className="button button--primary" type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Signing in…' : 'Sign in with email'}
      </button>
    </form>
  )

  return (
    <div className={`sign-in sign-in--split${oidc ? ' sign-in--prod' : ' sign-in--dev'}`}>
      <aside className="sign-in__panel sign-in__panel--brand">
        <div className="sign-in__brand-inner">
          <p className="sign-in__eyebrow">
            {oidc ? 'Veersa Tech · Production' : 'ARIES · Development'}
          </p>
          <h1 className="sign-in__brand-title">ARIES</h1>
          <p className="sign-in__brand-tagline">
            Agentic Revenue Intelligence &amp; Estimation System
          </p>
          <p className="sign-in__about">{ABOUT_BLURB}</p>
          <ul className="sign-in__pillars">
            <li>Owner-owned pipeline with Finance oversight</li>
            <li>Impact preview before every material change</li>
            <li>
              {oidc
                ? 'Sign in with your Veersa Tech Microsoft account'
                : 'Local email/password for development'}
            </li>
          </ul>
          <SignInVisual />
        </div>
      </aside>

      <main className="sign-in__panel sign-in__panel--form">
        <div className="sign-in__form-wrap">
          <h2 className="sign-in__title">{oidc ? 'Sign in to production' : 'Sign in'}</h2>
          <p className="sign-in__lede">
            {oidc
              ? 'Use your Veersa Tech Microsoft account. Access still requires an ARIES role on the Finance access list.'
              : 'Development environment — use a local seed account or any provisioned email/password.'}
          </p>

          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}

          {oidc ? (
            <>
              <button
                type="button"
                className="button button--primary sign-in__ms-btn"
                onClick={() => void handleMicrosoft()}
                disabled={msPending}
              >
                {msPending ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
              </button>
              <p className="sign-in__ms-hint">
                You will be redirected to login.microsoftonline.com
              </p>

              <details
                className="sign-in__break-glass"
                open={breakGlassOpen}
                onToggle={(event) => setBreakGlassOpen((event.target as HTMLDetailsElement).open)}
              >
                <summary>Admin emergency sign-in (password)</summary>
                <p className="sign-in__break-glass-note">
                  Break-glass only. Prefer Microsoft SSO for day-to-day production access.
                </p>
                {passwordForm}
              </details>
            </>
          ) : (
            <>
              {passwordForm}
              {import.meta.env.DEV && (
                <aside className="sign-in__dev" aria-label="Local development accounts">
                  <p className="sign-in__dev-title">Local development</p>
                  <p className="sign-in__dev-password">
                    Password for all seed users: <code>{DEV_SEED_PASSWORD}</code>
                  </p>
                  <ul className="sign-in__dev-list">
                    {DEV_SEED_USERS.map((user) => (
                      <li key={user.email}>
                        <button
                          type="button"
                          className="sign-in__dev-user"
                          onClick={() => fillDevUser(user.email)}
                        >
                          <span className="sign-in__dev-email">{user.email}</span>
                          <span className="sign-in__dev-role">{user.role}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="sign-in__dev-hint">
                    Click a user to fill the form. Seed with --seed if login fails.
                  </p>
                </aside>
              )}
            </>
          )}

          <AppVersion className="sign-in__version" />
        </div>
      </main>
    </div>
  )
}
